// The city view: the walled city in its countryside under a camera (pinch-to-zoom, Oct 9 2026 — plan:
// claude/valor-web-city-zoom-plan.md). Since Oct 12 2026 the art is the Hearthstone Refined city (City View handoff, site
// version 20; game/cityScene.ts): one 1181 x 1343 frame — the shared terrain, then the master's layers in its stack order,
// each building wearing the look of its own level (an empty plot draws nothing), the streets in the City Hall tier's
// material with every building's own entrance spur, the walls and gate by the Walls' level, the Wheel, the Rally Point and
// the loyalty flag — all of it in one `world` layer moved by one CSS transform (camera.ts). The plates (level disc,
// nameplate, status pill), the construction pop-up and the stage buttons live in screen space over it and never scale.
//
// Zoom runs from 0.5x (the Overview: the whole walled city, with no levels or names — Ben, Oct 10 2026) to 2.5x; 1x is
// the whole city. Labels change with zoom (cameraMath.lodOf): full close up, the player's choice around 1x (Settings ›
// Display › City labels), level discs farther out, fading to none at the overview. Tapping a building (its plate, or the
// building itself — its painted outline, cityScene.hitAt) opens its screen. The hammer turns on construction mode
// (Construction.tsx): a tapped building or empty plot then gets the live game's pop-up — next level's cost and time,
// UPGRADE, SLIDE TO DOWNGRADE — and the camera eases so the building and its pop-up fit. The gate is the Walls. The Wheel
// and the loyalty flag open their screens. (the server clock moved to the header's resource strip, Classic Frame Oct 10 2026)
import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState, type MouseEvent as RMouseEvent, type PointerEvent as RPointerEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { Village } from "@/api/types";
import { art } from "@/kit/art";
import { Countdown, LevelDisc } from "@/kit/city";
import { IconClose, IconHammer, IconHeart, IconHourglass, IconSwords, IconCrown, IconCoins, IconFit } from "@/kit/icons";
import { Disc, cx } from "@/kit/primitives";
import { useNow } from "@/state/clock";
import { usePrefs } from "@/state/prefs";
import { BUILDING_KEYS, BUILDING_NAMES, type BuildingKey } from "@/game/constants";
import { abbrev, fmtNum } from "@/game/format";
import { buildingMap, constructionQueue, freePopulation, fullInMs, movements, queueSlots, recruitQueue, researchQueue } from "@/game/city";
import { MAX_REL, OVERVIEW_REL, WHOLE_EPS, focusFor, lodOf, openingCam, overviewT, relOf, type Cam, type View } from "@/game/cameraMath";
import {
  OWNERS, OWNER_OF, SCENE, hitAt, hitTargets, layersFor, lookFor, popAnchor, type HitTarget, type Levels, type Owner, type SceneLayer,
} from "@/game/cityScene";
import { CityPage, useCity } from "./CityRoute";
import { BuildPopover, QueuePanel, plateReadiness, type PopAnchor } from "./Construction";
import { CityCamera, loadSavedView } from "./camera";
import "./city.css";

/** The city view in construction mode: `key` selects that building (its pop-up opens), `queue` drops the queue down.
 *  Construction mode is the only place to build (Ben, Oct 8) — every "build this" link in the app comes through here. */
export function buildLink(base: string, key?: BuildingKey, queue?: boolean): string {
  return `${base}?build=${key ?? "1"}${queue ? "&queue=1" : ""}`;
}

/** The world map, opened from the city view by zooming out past the overview: it centres on this city (the session's) and
 *  plays a short zoom-out from it (`from=city`). */
export function mapFrom(base: string): string {
  return `${base.replace(/\/city\/[^/]+$/, "")}/map?from=city`;
}

/** Where a plate leads. */
export function screenFor(base: string, key: BuildingKey | "wheel"): string {
  switch (key) {
    case "b_hq": return `${base}/hall`;
    case "b_b": case "b_st": case "b_wo": return `${base}/recruit/${key}`;
    case "b_s": return `${base}/forge`;
    case "b_a": return `${base}/academy`;
    case "b_r": return `${base}/rally`;
    case "b_m": return `${base}/market`;
    case "wheel": return `${base}/wheel`;
    default: return `${base}/b/${key}`;
  }
}

/** The plates, top to bottom by where they hang (the reading order on screen): the fifteen buildings and the Wheel. */
const PLATES: { key: BuildingKey | "wheel"; owner: Owner; lx: number; ly: number }[] = [
  ...BUILDING_KEYS.map((k) => ({ key: k as BuildingKey | "wheel", owner: OWNER_OF[k] })),
  { key: "wheel" as const, owner: "wheel" as Owner },
]
  .map((p) => ({ ...p, lx: OWNERS[p.owner].label![0], ly: OWNERS[p.owner].label![1] }))
  .sort((a, b) => a.ly - b.ly || a.lx - b.lx);
/** The loyalty plate stands on the flag's crown and grows upward (the Rally Point's plate hangs just below and right of
 *  the flag's foot), as the old city view's did. */
const LOYALTY_LABEL: [number, number] = [(OWNERS.flag.looks["1"].bbox[0] + OWNERS.flag.looks["1"].bbox[2]) / 2, OWNERS.flag.looks["1"].bbox[1] - 4];

/** A finger's slack around a building's outline, CSS px. */
const TAP_SLACK = 10;

export function CityView() {
  const { v, vid, base } = useCity();
  const nav = useNavigate();
  return (
    <CityPage layout="full">
      <div className="stage-scroll cam">
        {v ? <Stage v={v} city={vid} base={base} go={nav} /> : <div className="stage-wrap"><div className="stage" /></div>}
      </div>
    </CityPage>
  );
}

function Stage({ v, city, base, go }: { v: Village; city: number; base: string; go: (to: string) => void }) {
  const now = useNow();
  const labels = usePrefs((s) => s.cityLabels);
  const levels = useMemo(() => Object.fromEntries((v.buildings ?? []).map((b) => [b.key, b.level])) as Levels, [v.buildings]);
  const levelsKey = BUILDING_KEYS.map((k) => levels[k] ?? 0).join(",");
  const bmap = useMemo(() => buildingMap(v), [v]);
  const queue = useMemo(() => constructionQueue(v), [v]);
  const training = useMemo(() => recruitQueue(v), [v]);
  const research = useMemo(() => researchQueue(v), [v]);
  const incoming = useMemo(() => movements(v).incoming.filter((m) => m.kind === "attack"), [v]);
  const slots = queueSlots(v);
  const queueFull = queue.length >= slots;

  // construction mode
  const [building, setBuilding] = useState(false);
  const [sel, setSel] = useState<BuildingKey | null>(null);
  const [queueOpen, setQueueOpen] = useState(false);
  const setMode = useCallback((on: boolean) => {
    setBuilding(on);
    setSel(null);
  }, []);
  // ?build=1 / ?build=<key> / &queue=1 (buildLink) open construction mode; the params are dropped once read
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const b = params.get("build");
    if (!b && !params.get("queue")) return;
    if (b) {
      setBuilding(true);
      setSel((BUILDING_KEYS as readonly string[]).includes(b) ? (b as BuildingKey) : null);
    }
    if (params.get("queue")) setQueueOpen(true);
    setParams({}, { replace: true });
  }, [params, setParams]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (document.querySelector(".scrim")) return; // a sheet above handles its own Escape
      if (sel) setSel(null);
      else if (queueOpen) setQueueOpen(false);
      else if (building) setMode(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [sel, queueOpen, building, setMode]);

  // ------------------------------------------------------------------ the art for this city's levels
  // (recomputed only when a level changes — a poll that changes nothing keeps every layer, and the camera, as they are)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const wanted = useMemo(() => ({ levels, layers: layersFor(levels) }), [levelsKey]);
  // what is on screen: a new set of looks replaces the old one whole, once all its pictures are in (useShownCity), so
  // the art, its tap outlines and the pop-up's anchor always describe the same city
  const track = useArtTracker();
  const { shown, arriving } = useShownCity(wanted, track, city);
  const layers = shown.layers;
  const targets = useMemo(() => hitTargets(shown.levels, building), [shown, building]);
  const artReady = useArtReady(layers, track, city);

  // ------------------------------------------------------------------ the camera
  const wrapRef = useRef<HTMLDivElement>(null);
  const vpRef = useRef<HTMLDivElement>(null);
  const worldRef = useRef<HTMLDivElement>(null);
  const [cam, setCam] = useState<CityCamera | null>(null);
  useLayoutEffect(() => {
    const vp = vpRef.current;
    const world = worldRef.current;
    if (!vp || !world) return;
    const c = new CityCamera(vp, world, SCENE.W, SCENE.H, SCENE.wallX0, SCENE.wallX1, OVERVIEW_REL, MAX_REL);
    c.set(openingCam(c.view, usePrefs.getState().cityOpens, loadSavedView(), SCENE.close));
    setCam(c);
    return () => c.destroy();
  }, []);

  // zooming out past the overview carries on to the world map (Ben, Oct 9): a pill shows how far the pull has got; at the
  // end, fingers let go (a wheel or the − key goes straight on) and the map opens on this city, zooming out from it
  const goRef = useRef(go);
  goRef.current = go;
  useEffect(() => {
    if (!cam) return;
    const vp = vpRef.current!;
    const pill = wrapRef.current!.querySelector<HTMLElement>(".map-pull")!;
    const txt = pill.querySelector<HTMLElement>(".mp-txt")!;
    cam.onPull = (t, by) => {
      vp.style.setProperty("--pull", String(t));
      pill.style.setProperty("--t", String(t));
      pill.classList.toggle("on", t > 0.02);
      pill.classList.toggle("ready", t >= 1);
      txt.textContent = t >= 1 ? (by === "touch" ? "Let go for the world map" : "To the world map") : "Zoom out for the world map";
    };
    cam.onMap = () => goRef.current(mapFrom(base));
    return () => {
      cam.onPull = null;
      cam.onMap = null;
    };
  }, [cam, base]);

  // the screen-space layer follows the camera every frame it moves
  const labelsRef = useRef(labels);
  labelsRef.current = labels;
  useEffect(() => {
    if (!cam) return;
    const wrap = wrapRef.current!;
    const vp = vpRef.current!;
    let lastRel = -1;
    let readT = 0;
    return cam.onFrame((c, view) => paint(wrap, vp, c, view, labelsRef.current, (rel) => {
      if (lastRel >= 0 && Math.abs(rel - lastRel) > 1e-4) {
        const r = wrap.querySelector<HTMLElement>(".zoom-read");
        if (r) {
          r.textContent = `${rel.toFixed(1)}×`;
          r.classList.add("on");
          clearTimeout(readT);
          readT = window.setTimeout(() => r.classList.remove("on"), 700);
        }
      }
      lastRel = rel;
    }));
  }, [cam]);
  // new plates / labels choice: redraw the screen-space layer
  useLayoutEffect(() => cam?.refresh());

  // e2e checks only (window.__VALOR_CITY_DEBUG__ set before the app loads): read-only views of the scene and the camera
  const debugRef = useRef({ layers, targets, cam });
  debugRef.current = { layers, targets, cam };
  useEffect(() => {
    if (!(window as unknown as { __VALOR_CITY_DEBUG__?: boolean }).__VALOR_CITY_DEBUG__) return;
    const hook = {
      layers: () => debugRef.current.layers.map((l) => l.id),
      targets: () => debugRef.current.targets.map((t) => ({ owner: t.owner, key: t.key, site: t.site, bbox: t.look.bbox })),
      hit: (x: number, y: number, tol = 0) => {
        const t = hitAt(debugRef.current.targets, x, y, tol);
        return t ? { owner: t.owner, key: t.key, site: t.site } : null;
      },
      cam: () => (debugRef.current.cam ? { ...debugRef.current.cam.cam, ...debugRef.current.cam.view } : null),
      /** puts the camera exactly at scale s with world point (wx, wy) at the window's top-left — evidence shots only */
      setCam: (s: number, x: number, y: number) => debugRef.current.cam?.set({ s, x, y }),
    };
    (window as unknown as { __valorCity?: typeof hook }).__valorCity = hook;
    return () => { delete (window as unknown as { __valorCity?: typeof hook }).__valorCity; };
  }, []);

  const tap = (key: HitTarget["key"]) => {
    if (key === "loyalty") {
      if (!building) go(`${base}/loyalty`);
      return;
    }
    if (building) {
      if (key === "wheel") return;
      setQueueOpen(false);
      setSel((s) => (s === key ? null : key));
    } else go(screenFor(base, key));
  };
  // a tap on the city: the building under it (its painted outline, front-most first; in construction mode empty plots
  // too), or open ground — which closes the pop-up
  const onStageClick = (e: RMouseEvent) => {
    if ((e.target as HTMLElement).closest("button")) return; // a plate or a stage button: its own click
    const t = targetAt(e.clientX, e.clientY);
    if (t) tap(t.key);
    else setSel(null);
  };
  const targetAt = (clientX: number, clientY: number): HitTarget | null => {
    const vp = vpRef.current;
    if (!vp || !cam) return null;
    const r = vp.getBoundingClientRect();
    const w = cam.toWorld(clientX - r.left, clientY - r.top);
    return hitAt(targets, w.x, w.y, TAP_SLACK / cam.cam.s);
  };
  // a mouse over a building shows it can be clicked
  const onStageMove = (e: RPointerEvent) => {
    if (e.pointerType !== "mouse" || e.buttons) return;
    const vp = vpRef.current;
    if (!vp) return;
    const on = !!targetAt(e.clientX, e.clientY);
    if (vp.classList.contains("over-bld") !== on) vp.classList.toggle("over-bld", on);
  };

  const hasHall = (levels.b_hq ?? 0) > 0;

  // where the pop-up points for the selected building (or its empty plot), frame px — on the look on screen
  const anchorW = sel ? popAnchor(OWNER_OF[sel], shown.levels) : null;
  const lit = building && sel ? OWNER_OF[sel] : null;

  return (
    <div ref={wrapRef} className={cx("stage-wrap", building && "building", artReady && "art-ready")}>
      <div
        className="stage"
        ref={vpRef}
        tabIndex={0}
        role="group"
        aria-label="The city — pinch or scroll to zoom, drag to look around; keys: + − 0 9 and arrows"
        aria-busy={!artReady}
        onClick={onStageClick}
        onPointerMove={onStageMove}
      >
        <div className="countryside" aria-hidden="true">
          <LayerImg layer={COUNTRYSIDE} track={track} url={art.city.countryside} />
        </div>
        <div className="world" ref={worldRef} style={{ width: SCENE.W, height: SCENE.H }}>
          <CityArt layers={layers} arriving={arriving?.layers ?? null} track={track} />
          <Outlines targets={targets} building={building} lit={lit} levels={shown.levels} scale={cam?.cam.s ?? 1} />
        </div>
        <div className="plates">
          {PLATES.map((p) => {
            const key = p.key;
            const level = key === "wheel" ? null : levels[key] ?? 0;
            const status = key === "wheel" ? wheelStatus(v) : plateStatus(v, key, level ?? 0, { queue, training, research, now });
            const ready = building && key !== "wheel" ? plateReadiness(v, bmap[key], queueFull) : null;
            return (
              <button
                key={key}
                type="button"
                data-lx={p.lx}
                data-ly={p.ly}
                data-key={key}
                className={cx("plate-btn", level === 0 && "dim", key === "wheel" && "aux", building && key === "wheel" && "inert", sel === key && "sel")}
                onClick={() => tap(key)}
                aria-disabled={building && key === "wheel" ? true : undefined}
                aria-expanded={building && key !== "wheel" ? sel === key : undefined}
                aria-label={`${key === "wheel" ? "Wheel of Fortune" : BUILDING_NAMES[key]}${level ? ` level ${level}` : level === 0 ? " not built" : ""}${building && key !== "wheel" ? " — construction" : ""}`}
              >
                <span className={cx("name", level === null && "noplate")}>
                  {level !== null && <LevelDisc level={level > 0 ? level : "·"} size="sm" dim={level === 0} />}
                  <span className="txt">{key === "wheel" ? "Wheel" : BUILDING_NAMES[key]}</span>
                  {ready && (ready === "max" ? <i className="cm-chip max">Max</i> : <i className={cx("cm-chip", ready)}><IconHammer /></i>)}
                </span>
                {status}
              </button>
            );
          })}
          {hasHall && (
            <button type="button" data-lx={LOYALTY_LABEL[0]} data-ly={LOYALTY_LABEL[1]} data-up="1" data-key="loyalty" className={cx("plate-btn aux", building && "inert")} onClick={() => tap("loyalty")} aria-label={`Loyalty ${v.loyalty} of 100`}>
              <span className="name noplate"><span className="txt">Loyalty</span></span>
              <span className={cx("status loyal", (v.loyalty < 100 || incoming.length > 0) && "threat")}><IconHeart /> {v.loyalty}/100</span>
            </button>
          )}
        </div>
      </div>
      <div className="stage-hud">
        <Disc label={building ? "Leave construction mode" : "Construction mode"} on={building} aria-pressed={building} className={cx("cm-hammer", building && "active")} onClick={() => setMode(!building)}>
          <IconHammer />
        </Disc>
        <button type="button" className={cx("cm-count", queueFull && "full", queueOpen && "open")} onClick={() => { setQueueOpen((o) => !o); setSel(null); }} aria-expanded={queueOpen} aria-label={`Construction queue: ${queue.length} of ${slots}`} title={`${queue.length} of ${slots} construction slots`}>
          {queue.length}
        </button>
      </div>
      {building && (
        <div className="cm-pill" role="status">
          <span>Construction Mode</span>
          <button type="button" aria-label="Leave construction mode" onClick={() => setMode(false)}><IconClose /></button>
        </div>
      )}
      {!artReady && <div className="stage-loading" role="status">Preparing the city…</div>}
      <div className="zoom-read" aria-hidden="true" />
      <div className="map-pull" aria-hidden="true">
        <img src={art.nav.map} alt="" />
        <span className="mp-txt">Zoom out for the world map</span>
        <span className="mp-bar"><i /></span>
      </div>
      <Disc size="sm" label="Whole city" className="fit-btn" aria-hidden="true" tabIndex={-1} onClick={() => cam?.toWhole()}>
        <IconFit />
      </Disc>
      {queueOpen && <QueuePanel queue={queue} slots={slots} onClose={() => setQueueOpen(false)} onHall={() => go(`${base}/hall`)} />}
      {building && sel && anchorW && cam && (
        <FollowPop
          key={sel}
          cam={cam}
          wrap={wrapRef}
          bkey={sel}
          at={anchorW}
          queue={queue}
          slots={slots}
          onClose={() => setSel(null)}
          onInfo={(levels[sel] ?? 0) > 0 || sel === "b_hq" ? () => go(screenFor(base, sel)) : undefined}
        />
      )}
    </div>
  );
}

// ------------------------------------------------------------------ the art

const srcOf = (l: { file: string }) => art.city.hearthstone(l.file);
const TERRAIN = { ...SCENE.terrain, id: "terrain", slot: "terrain", owner: "terrain" } as SceneLayer;
const COUNTRYSIDE = { id: "countryside", file: "countryside.webp", slot: "countryside", owner: "terrain", x: 0, y: 0, w: SCENE.W * 3, h: SCENE.H * 3 } as SceneLayer;
/** Where the backdrop's clearing actually sits (template-matched against the terrain, Oct 13 2026), and the widest it
 *  is ever drawn, CSS px (art-src/city-countryside.prompt.txt). */
const COUNTRYSIDE_DX = 26;
const COUNTRYSIDE_DY = 39;
const COUNTRYSIDE_MAX_PX = 6144;
/** From here in the sharp 3x backdrop replaces the 1x one. Drawn at the overview, its size made Chrome raster the whole
 *  city softer (Oct 13 2026 mockup), and there the 1x file is sharp enough. */
const COUNTRYSIDE_HD_REL = 0.9;
/** Point the backdrop at the 1x picture (the one the arrival gate waited for) or at the 3x one. A changed src keeps
 *  showing the old picture until the new one has loaded (HTML's pending image request), so the swap never blanks; and
 *  nothing else holds the 3x picture, so zooming back out releases it. A src React rendered stays React's: it only
 *  re-sets the attribute when its own value changes. */
function countrysideRes(img: HTMLImageElement, hd: boolean) {
  const lo = img.dataset.lo ?? img.getAttribute("src");
  if (!lo || lo === art.city.countrysideHD) return;
  img.dataset.lo = lo;
  const want = hd ? art.city.countrysideHD : lo;
  if (img.getAttribute("src") !== want) img.setAttribute("src", want);
}
/** How long the city waits for its art before it shows anyway (a slow or failing file can't hold it back for good). */
const ARRIVAL_MS = 8000;

/** Which layers' pictures are in on the page: each layer's <img> reports once it has loaded and decoded (or failed — a
 *  missing file can't hold the city back) and forgets it when it leaves. Counting the <img> elements themselves, not
 *  the URLs, means a picture is never swapped in before the element that shows it has it, whatever the cache does. */
interface ArtTracker {
  done: Set<string>;
  bump: () => void;
}
function useArtTracker(): ArtTracker {
  const [, bump] = useReducer((n: number) => n + 1, 0);
  return useState<ArtTracker>(() => ({ done: new Set(), bump }))[0];
}
const allIn = (t: ArtTracker, layers: SceneLayer[]) => t.done.has(COUNTRYSIDE.id) && t.done.has(TERRAIN.id) && layers.every((l) => t.done.has(l.id));

/** True once the terrain and every layer of this city's looks are in (at most ARRIVAL_MS — then it shows as it is), so a
 *  city appears whole rather than building by building: on opening, and again on a switch to another city (the camera
 *  stays). Within a city, later changes swap without hiding it (useShownCity). A city whose pictures the browser already
 *  has is in within a frame or two (the "Preparing the city…" note only fades in after a moment, city.css). */
function useArtReady(layers: SceneLayer[], track: ArtTracker, city: number): boolean {
  const [readyFor, setReadyFor] = useState<number | null>(null);
  const now = readyFor === city || waitedFor.has(city) || allIn(track, layers);
  useEffect(() => {
    if (now) {
      if (readyFor !== city) setReadyFor(city);
      return;
    }
    const t = window.setTimeout(() => {
      waitedFor.add(city);
      setReadyFor(city);
    }, ARRIVAL_MS);
    return () => clearTimeout(t);
  }, [now, readyFor, city]);
  return now;
}
/** Cities whose art once kept the arrival gate waiting its full ARRIVAL_MS (a file that hangs): they aren't made to
 *  wait again this visit. */
const waitedFor = new Set<number>();

/** The looks the city shows, and the ones on their way. A level change — the 15 s poll, an upgrade finishing — first
 *  loads the new looks' pictures hidden, then swaps the art whole once they are all in (or ARRIVAL_MS has passed): a
 *  building never vanishes while its new look loads (the City Hall's tier IV is one layer in another stack slot than
 *  its tier III's two), and the walls' four pieces never show two tiers at once. Another city is never held: its art
 *  replaces this one's at once, behind the arrival gate (useArtReady), so one city's buildings never stand under
 *  another's name and plates. */
function useShownCity<T extends { layers: SceneLayer[] }>(want: T, track: ArtTracker, city: number): { shown: T; arriving: T | null } {
  const [shown, setShown] = useState({ want, city });
  const [late, setLate] = useState<T | null>(null);
  const now = shown.want === want || shown.city !== city || late === want || allIn(track, want.layers) ? want : shown.want;
  useEffect(() => {
    if (now === want) {
      if (shown.want !== want || shown.city !== city) setShown({ want, city });
      return;
    }
    const t = window.setTimeout(() => setLate(want), ARRIVAL_MS);
    return () => clearTimeout(t);
  }, [want, now, shown, city]);
  return { shown: now, arriving: now === want ? null : want };
}

/** The shared terrain and the city's layers, back to front, at their places in the frame (frame px = world units), one
 *  <img> per layer; the arriving looks' pictures load hidden after them. On the swap those same elements are moved into
 *  the stack and shown — picture and place together, nothing reloaded. */
const CityArt = memo(function CityArt({ layers, arriving, track }: { layers: SceneLayer[]; arriving: SceneLayer[] | null; track: ArtTracker }) {
  const showing = new Set(layers.map((l) => l.id));
  const items = [
    ...layers.map((l) => ({ l, hidden: false })),
    ...(arriving ?? []).filter((l) => !showing.has(l.id)).map((l) => ({ l, hidden: true })),
  ];
  return (
    <>
      <LayerImg key={TERRAIN.id} layer={TERRAIN} track={track} />
      {items.map(({ l, hidden }) => <LayerImg key={l.id} layer={l} hidden={hidden} track={track} />)}
    </>
  );
});

/** Each picture is fetched once per visit and kept as a blob: the art is served `max-age=0, must-revalidate`, so a new
 *  <img> for a URL the page showed before asks the server again, and coming back to the city (from a building's screen,
 *  another city) would wait a round trip for every layer. A blob: URL needs none. At most the 187 files (12.3 MB), in
 *  practice a city or two's looks. A file that fails (404, network) gets no blob and its <img> uses the URL itself. */
const blobs = new Map<string, string>();
const fetching = new Map<string, Promise<void>>();
function fetchArt(url: string): Promise<void> {
  let p = fetching.get(url);
  if (!p) {
    p = fetch(url)
      .then((r) => (r.ok ? r.blob() : null))
      .then((b) => {
        if (b && b.size) blobs.set(url, URL.createObjectURL(b));
      })
      .catch(() => undefined);
    fetching.set(url, p);
  }
  return p;
}

function LayerImg({ layer, hidden = false, track, url = srcOf(layer) }: { layer: SceneLayer; hidden?: boolean; track: ArtTracker; url?: string }) {
  const ref = useRef<HTMLImageElement>(null);
  const arriving = useRef(hidden).current;
  // its blob once fetched (at once for a picture this visit has fetched before), else the URL if the fetch failed
  const [src, setSrc] = useState<string | null>(() => blobs.get(url) ?? null);
  useEffect(() => {
    if (src) return;
    let live = true;
    void fetchArt(url).then(() => live && setSrc(blobs.get(url) ?? url));
    return () => {
      live = false;
    };
  }, [url, src]);
  useLayoutEffect(() => {
    const im = ref.current!;
    if (!src) return;
    let live = true;
    const mark = () => {
      if (!live || track.done.has(layer.id)) return;
      track.done.add(layer.id);
      im.classList.add("ready");
      track.bump();
    };
    // An arriving look's picture is decoded before it counts, so the swap shows it whole (onload is the authority —
    // WebKit's decode() can reject a good image, and then it counts anyway). A city appearing needn't wait for that:
    // its fade-in covers the decode.
    const loaded = () => {
      if (arriving && typeof im.decode === "function") im.decode().then(mark, mark);
      else mark();
    };
    if (im.complete) {
      if (im.naturalWidth > 0) loaded();
      else mark(); // a file that failed before this element had it
    }
    im.addEventListener("load", loaded);
    im.addEventListener("error", mark);
    return () => {
      live = false;
      im.removeEventListener("load", loaded);
      im.removeEventListener("error", mark);
      track.done.delete(layer.id);
    };
  }, [layer.id, src, track, arriving]);
  const sprite = layer.slot.startsWith("building:") || layer.slot.startsWith("fixture:") || layer.slot === "walls:gate";
  return (
    <img
      ref={ref}
      // "sprite": a building's (or the gate's, the Wheel's, the Rally Point's, the flag's) own art; "ready" once it is in;
      // "pending": an arriving look's picture, loading hidden
      className={cx("layer", layer.id === TERRAIN.id && "terrain", sprite && "sprite", track.done.has(layer.id) && "ready", hidden && "pending")}
      src={src ?? undefined}
      alt=""
      draggable={false}
      decoding="async"
      data-id={layer.id === TERRAIN.id ? undefined : layer.id}
      data-owner={layer.id === TERRAIN.id ? undefined : layer.owner}
      data-src={url}
      style={{ left: layer.x, top: layer.y, width: layer.w, height: layer.h }}
    />
  );
}

/** The outlines' line widths, dashes and shadow for camera scale `s`, in the world's own px — so they keep one width on
 *  screen at any zoom (`vector-effect: non-scaling-stroke` doesn't see through the world layer's CSS transform). */
function outlineInk(s: number): Record<string, string> {
  const k = 1 / (s || 1);
  return {
    "--sw-site": `${(1.6 * k).toFixed(3)}px`,
    "--sw-on": `${(2.2 * k).toFixed(3)}px`,
    "--dash": `${(6 * k).toFixed(3)} ${(4 * k).toFixed(3)}`,
    "--blur": `${(1.5 * k).toFixed(3)}px`,
  };
}

/** Construction mode's marks, drawn in the world under the plates: each empty plot's outline (dashed — the ground its
 *  first look will stand on) and the selected building's outline in gold. Lines keep their width at any zoom (paint()
 *  re-inks them every camera frame). */
function Outlines({ targets, building, lit, levels, scale }: { targets: HitTarget[]; building: boolean; lit: Owner | null; levels: Levels; scale: number }) {
  if (!building) return null;
  const sel = lit ? lookFor(lit, levels).look : null;
  const d = (polys: number[][]) => polys.map((p) => `M${p[0]} ${p[1]}${Array.from({ length: p.length / 2 - 1 }, (_, i) => `L${p[2 * i + 2]} ${p[2 * i + 3]}`).join("")}Z`).join("");
  return (
    <svg className="outlines" viewBox={`0 0 ${SCENE.W} ${SCENE.H}`} width={SCENE.W} height={SCENE.H} aria-hidden="true" style={outlineInk(scale) as React.CSSProperties}>
      {targets.filter((t) => t.site).map((t) => <path key={t.owner} className="site" data-owner={t.owner} d={d(t.look.hit)} />)}
      {sel && <path className="on" d={d(sel.hit)} />}
    </svg>
  );
}

/** Positions everything in screen space for one camera frame (no React render — this runs every frame the camera moves). */
function paint(wrap: HTMLElement, vp: HTMLElement, c: Cam, view: View, labels: ReturnType<typeof usePrefs.getState>["cityLabels"], onRel: (rel: number) => void) {
  const rel = relOf(view, c);
  const lod = lodOf(rel, labels);
  if (vp.dataset.lod !== lod) vp.dataset.lod = lod;
  const t = overviewT(rel);
  // The surrounding forest follows the city. Cover even a wide screen or an overscroll, without repeating the art;
  // only the shared terrain's outermost pixels soften (buildings and their geometry stay intact).
  const countryside = vp.querySelector<HTMLImageElement>(".countryside img");
  if (countryside) {
    // It keeps the city's own scale (so its trees match the terrain's), within a raster-size limit for phones.
    const s = Math.min(c.s, COUNTRYSIDE_MAX_PX / COUNTRYSIDE.w);
    const w = Math.max(COUNTRYSIDE.w * s, view.vw + 2, (view.vh + 2) * SCENE.W / SCENE.H);
    const h = w * SCENE.H / SCENE.W;
    // its central clearing sits COUNTRYSIDE_DX/DY world px right of / below the backdrop's middle (matched to the terrain)
    const x = Math.max(view.vw - w, Math.min(0, c.x + (SCENE.W / 2 - COUNTRYSIDE_DX) * c.s - w / 2));
    const y = Math.max(view.vh - h, Math.min(0, c.y + (SCENE.H / 2 - COUNTRYSIDE_DY) * c.s - h / 2));
    countryside.style.width = `${w}px`;
    countryside.style.height = `${h}px`;
    countryside.style.transform = `translate3d(${x}px,${y}px,0)`;
    countrysideRes(countryside, rel >= COUNTRYSIDE_HD_REL);
  }
  // the terrain's outer edge always softens a little into the countryside (no hard seam), more at the overview
  vp.style.setProperty("--terrain-feather", `${20 + 28 * t}px`);
  // plates on the buildings: off-screen ones hide; they fade out towards the overview
  for (const el of vp.querySelectorAll<HTMLElement>(".plates .plate-btn")) {
    const x = Number(el.dataset.lx) * c.s + c.x;
    const y = Number(el.dataset.ly) * c.s + c.y;
    const off = x < -150 || x > view.vw + 150 || y < (el.dataset.up ? -20 : -60) || y > view.vh + (el.dataset.up ? 60 : 20);
    el.style.visibility = off || t >= 0.999 ? "hidden" : "";
    if (!off) el.style.transform = `translate3d(${x}px,${y}px,0) ${el.dataset.up ? "translate(-50%,-100%)" : "translateX(-50%)"}`;
    el.style.opacity = t > 0 ? String(1 - t) : "";
    el.style.pointerEvents = t > 0.5 ? "none" : "";
  }
  // construction mode's outlines keep one width on screen
  const ol = vp.querySelector<SVGSVGElement>("svg.outlines");
  if (ol) for (const [k, val] of Object.entries(outlineInk(c.s))) ol.style.setProperty(k, val);
  // (no callouts or leader lines at the overview any more — Ben, Oct 10 2026: no levels or names fully zoomed out)
  // the whole-city button shows whenever the camera isn't at 1x
  const fit = wrap.querySelector<HTMLElement>(".fit-btn");
  if (fit) {
    const away = Math.abs(rel - 1) > WHOLE_EPS;
    fit.setAttribute("aria-hidden", String(!away));
    fit.tabIndex = away ? 0 : -1;
  }
  onRel(rel);
}

/** The construction pop-up, kept on its building as the camera moves. Opening it eases the camera so the building and
 *  the pop-up fit (cameraMath.focusFor). */
function FollowPop({ cam, wrap, bkey, at, ...rest }: {
  cam: CityCamera; wrap: React.RefObject<HTMLDivElement | null>; bkey: BuildingKey; at: { x: number; above: number; below: number };
  queue: Parameters<typeof BuildPopover>[0]["queue"]; slots: number; onClose: () => void; onInfo?: () => void;
}) {
  const [a, setA] = useState<{ anchor: PopAnchor; w: number } | null>(null);
  useEffect(
    () =>
      cam.onFrame((c, view) => {
        const anchor = { x: at.x * c.s + c.x, above: at.above * c.s + c.y, below: at.below * c.s + c.y };
        setA((o) => (o && o.w === view.vw && o.anchor.x === anchor.x && o.anchor.above === anchor.above && o.anchor.below === anchor.below ? o : { anchor, w: view.vw }));
      }),
    [cam, at.x, at.above, at.below],
  );
  // once it has a size, bring the building and the pop-up into view
  const focused = useRef(false);
  useLayoutEffect(() => {
    if (focused.current || !a) return;
    // (measured while it is still hidden — its own layout effect places it — so the camera starts moving at once)
    const el = wrap.current?.querySelector<HTMLElement>(".cm-pop");
    if (!el || !el.offsetHeight) return;
    focused.current = true;
    const target = focusFor(cam.view, cam.cam, at.x, at.above, el.offsetHeight);
    if (target) cam.animateTo(target, 380);
  });
  if (!a) return null;
  return <BuildPopover bkey={bkey} anchor={a.anchor} stageW={a.w} {...rest} />;
}

function plateStatus(
  v: Village,
  key: BuildingKey,
  level: number,
  ctx: { queue: ReturnType<typeof constructionQueue>; training: ReturnType<typeof recruitQueue>; research: ReturnType<typeof researchQueue>; now: number },
) {
  const job = ctx.queue.find((q) => q.item.key === key);
  if (job && ctx.queue[0] === job) return <span className="status work"><IconHammer /> Lv. {job.item.level} · <Countdown endMs={job.endMs} /></span>;
  if (job) return <span className="status"><IconHourglass /> Lv. {job.item.level} queued</span>;
  if (level === 0) return null;
  switch (key) {
    case "b_b": case "b_st": case "b_wo": {
      const t = ctx.training.filter((e) => e.building === key);
      if (t.length) return <span className="status work"><IconSwords /> {fmtNum(t.reduce((a, e) => a + e.item.num_units, 0))} training · <Countdown endMs={t[t.length - 1].endMs} /></span>;
      return null;
    }
    case "b_a": {
      const t = ctx.training.filter((e) => e.building === "b_a");
      if (t.length) return <span className="status work"><IconCrown /> Scholar · <Countdown endMs={t[0].endMs} /></span>;
      return null;
    }
    case "b_s": {
      const r = ctx.research[0];
      if (r) return <span className="status work"><IconCrown /> {r.item.name} {r.item.level} · <Countdown endMs={r.endMs} /></span>;
      return null;
    }
    case "b_w": return <span className="status"><img src={art.hud.wood} alt="" /> {fmtNum(v.resources_production?.r_w)}/hr</span>;
    case "b_c": return <span className="status"><img src={art.hud.clay} alt="" /> {fmtNum(v.resources_production?.r_c)}/hr</span>;
    case "b_i": return <span className="status"><img src={art.hud.iron} alt="" /> {fmtNum(v.resources_production?.r_i)}/hr</span>;
    case "b_f": return <span className="status"><img src={art.hud.food} alt="" /> {fmtNum(freePopulation(v))} left</span>;
    case "b_wh": {
      const soonest = Math.min(fullInMs(v, "r_w"), fullInMs(v, "r_c"), fullInMs(v, "r_i"));
      return <span className={cx("status", soonest <= 0 && "threat")}><img src={art.icon.castle} alt="" /> {soonest <= 0 ? "Full" : `${abbrev(v.resources_capacity)} cap`}</span>;
    }
    case "b_h": {
      const hidden = v.building_details?.b_h?.current_capacity;
      return hidden ? <span className="status"><img src={art.icon.castle} alt="" /> {abbrev(hidden)} hidden</span> : null;
    }
    case "b_r": {
      const inc = movements(v).incoming.filter((m) => m.kind === "attack");
      if (inc.length) return <span className="status threat"><IconSwords /> {inc.length} incoming · <Countdown endMs={inc[0].endMs} /></span>;
      const out = movements(v).outgoing.filter((m) => m.kind === "attack" || m.kind === "support");
      if (out.length) return <span className="status"><IconSwords /> {out.length} out</span>;
      return null;
    }
    case "b_m": {
      const free = v.market_info?.available_merchants;
      const total = v.market_info?.total_merchants;
      return total ? <span className="status"><img src={art.market.cart} alt="" /> {free}/{total} merchants</span> : null;
    }
    default: return null;
  }
}

function wheelStatus(v: Village) {
  const n = v.amulet?.num_owned ?? 0;
  return n > 0 ? <span className="status work"><IconCoins /> {fmtNum(n)} amulet{n === 1 ? "" : "s"}</span> : null;
}
